import React, { useEffect, useMemo, useState } from 'react';
import { Half, halfYearMonths } from './vat/period';
import { parseNaverFile } from './vat/parseNaverTaxReport';
import { parseCoupangFile } from './vat/parseCoupang';
import { parseEasyCheckFile } from './vat/parseEasyCheck';
import { parseHometaxFile } from './vat/parseHometaxInvoice';
import { combineRollups, hometaxRollup, tumblbugRollup, TotalRow } from './vat/rollup';
import { buildVatExcelBlob, VatExcelInput } from './vat/buildVatExcel';
import { mergeByMonth } from './vat/merge';
import { loadDraft, saveDraft, clearDraft, emptyDraft, NaverKey } from './vat/draft';
import { HometaxMonthRow, TumblbugMonthRow, MonthlyRollup } from './vat/types';

const NAVER_LABELS: Record<NaverKey, string> = {
  제주: '스마트스토어(제주로부터)', 남해: '스마트스토어(남해로부터)', 해피빈: '해피빈',
};

export default function VatFiling() {
  const now = new Date();
  const initialYear = now.getFullYear();
  const initialHalf: Half = now.getMonth() + 1 <= 6 ? '상반기' : '하반기';
  const [initialDraft] = useState(() => loadDraft(initialYear, initialHalf));

  const [year, setYear] = useState(initialYear);
  const [half, setHalf] = useState<Half>(initialHalf);
  const months = useMemo(() => halfYearMonths(year, half), [year, half]);

  const [naver, setNaver] = useState(initialDraft.naver);
  const [coupang, setCoupang] = useState(initialDraft.coupang);
  const [easyCheck, setEasyCheck] = useState(initialDraft.easyCheck);
  const [hometax, setHometax] = useState(initialDraft.hometax);
  const [tumblbug, setTumblbug] = useState(initialDraft.tumblbug);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState('');
  const [saved, setSaved] = useState(false);

  // 자동 저장: 기간별로 분리 저장(vat-draft-{year}-{half}). 상태가 바뀔 때마다 저장.
  useEffect(() => {
    saveDraft(year, half, { naver, coupang, easyCheck, hometax, tumblbug });
    setSaved(true);
  }, [year, half, naver, coupang, easyCheck, hometax, tumblbug]);

  const showToast = (m: string) => { setToast(m); setTimeout(() => setToast(''), 2500); };
  const fmt = (n: number) => n.toLocaleString();

  const tumblbugFor = (month: string): TumblbugMonthRow =>
    tumblbug.find(t => t.month === month) || { month, exempt: 0 };

  const setTumblbugCell = (month: string, value: number) => {
    setTumblbug(prev => {
      const others = prev.filter(t => t.month !== month);
      return value ? [...others, { month, exempt: value }] : others;
    });
  };

  // 기간 변경 시 해당 기간의 임시저장 초안을 불러온다(기간별 분리 보관).
  const changePeriod = (nextYear: number, nextHalf: Half) => {
    const d = loadDraft(nextYear, nextHalf);
    setYear(nextYear);
    setHalf(nextHalf);
    setNaver(d.naver);
    setCoupang(d.coupang);
    setEasyCheck(d.easyCheck);
    setHometax(d.hometax);
    setTumblbug(d.tumblbug);
  };

  const resetPeriod = () => {
    if (!window.confirm(`${year}년 ${half} 임시저장을 삭제하고 초기화할까요?`)) return;
    clearDraft(year, half);
    const e = emptyDraft();
    setNaver(e.naver);
    setCoupang(e.coupang);
    setEasyCheck(e.easyCheck);
    setHometax(e.hometax);
    setTumblbug(e.tumblbug);
    showToast('임시저장을 삭제했습니다');
  };

  const rollups: MonthlyRollup[][] = useMemo(() => {
    const naverRollups = (Object.keys(naver) as NaverKey[]).map(k =>
      naver[k].map(r => ({ month: r.month, taxable: r.taxable, exempt: r.exempt })));
    return [
      ...naverRollups,
      coupang.map(r => ({ month: r.month, taxable: 0, exempt: r.total })),
      easyCheck.map(r => ({ month: r.month, taxable: r.taxable, exempt: r.exempt })),
      hometaxRollup(hometax),
      tumblbugRollup(tumblbug),
    ];
  }, [naver, coupang, easyCheck, hometax, tumblbug]);

  const total: TotalRow[] = useMemo(() => combineRollups(rollups, months), [rollups, months]);

  // 월 기준 누적: 여러 파일(월별/취합)을 순서대로 파싱해 mergeByMonth 로 합침.
  // 같은 달을 다시 올리면 그 달만 교체되어 이중집계되지 않는다.
  const handleNaver = async (key: NaverKey, files?: FileList) => {
    if (!files || files.length === 0) return;
    setBusy(NAVER_LABELS[key]);
    try {
      let rows = naver[key];
      for (const f of Array.from(files)) {
        const res = await parseNaverFile(f, year, half, NAVER_LABELS[key]);
        rows = mergeByMonth(rows, res.rows);
      }
      setNaver(prev => ({ ...prev, [key]: rows }));
      showToast(`${NAVER_LABELS[key]}: ${files.length}개 파일 · 현재 ${rows.length}개월`);
    } catch { showToast('파싱 실패 - 파일 확인'); }
    setBusy('');
  };
  const handleCoupang = async (files?: FileList) => {
    if (!files || files.length === 0) return; setBusy('쿠팡');
    try {
      let rows = coupang;
      for (const f of Array.from(files)) rows = mergeByMonth(rows, (await parseCoupangFile(f, year, half)).rows);
      setCoupang(rows);
      showToast(`쿠팡: ${files.length}개 파일 · 현재 ${rows.length}개월`);
    } catch { showToast('파싱 실패 - 파일 확인'); }
    setBusy('');
  };
  const handleEasy = async (files?: FileList) => {
    if (!files || files.length === 0) return; setBusy('오프라인(이지체크)');
    try {
      let rows = easyCheck;
      for (const f of Array.from(files)) rows = mergeByMonth(rows, (await parseEasyCheckFile(f, year, half)).rows);
      setEasyCheck(rows);
      showToast(`오프라인: ${files.length}개 파일 · 현재 ${rows.length}개월`);
    } catch { showToast('파싱 실패 - 파일 확인'); }
    setBusy('');
  };
  // 홈택스는 과세(세금계산서)·면세(계산서) 파일이 나뉘어 같은 달을 합산해야 하므로,
  // 선택한 파일들을 월별로 합산해 채널을 통째로 대체한다(과세·면세 파일을 한 번에 선택).
  const handleHometax = async (files?: FileList) => {
    if (!files || files.length === 0) return; setBusy('홈택스');
    try {
      const parsed = await Promise.all(Array.from(files).map(f => parseHometaxFile(f, year, half)));
      const byMonth = new Map<string, HometaxMonthRow>();
      for (const res of parsed) for (const r of res.rows) {
        const cur = byMonth.get(r.month) ?? { month: r.month, exempt: 0, taxable: 0 };
        cur.exempt += r.exempt; cur.taxable += r.taxable;
        byMonth.set(r.month, cur);
      }
      const rows = Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month));
      setHometax(rows);
      showToast(`홈택스: ${files.length}개 파일 · 현재 ${rows.length}개월`);
    } catch { showToast('파싱 실패 - 파일 확인'); }
    setBusy('');
  };

  const download = async () => {
    const input: VatExcelInput = {
      total,
      naver: (Object.keys(naver) as NaverKey[]).map(k => ({ channel: NAVER_LABELS[k], rows: naver[k] })),
      coupang, easyCheck, hometax, tumblbug,
    };
    const blob = await buildVatExcelBlob(input);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${year}년_${half}_부가세신고.xlsx`;
    a.click(); URL.revokeObjectURL(url);
  };

  const UploadSlot = ({ label, loaded, onFiles }: { label: string; loaded: number; onFiles: (files: FileList) => void }) => (
    <label className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3 cursor-pointer hover:border-jeju-brown transition-colors">
      <span className="text-sm text-gray-700">
        {label}
        {loaded > 0 && <span className="ml-2 text-xs text-jeju-brown">· {loaded}개월 반영</span>}
      </span>
      <span className="text-xs text-gray-400">{busy === label ? '처리 중...' : '엑셀 선택 (여러 개 가능)'}</span>
      <input type="file" accept=".xlsx,.xls" multiple className="hidden"
        onChange={e => { if (e.target.files && e.target.files.length) onFiles(e.target.files); e.currentTarget.value = ''; }} />
    </label>
  );

  return (
    <div>
      <h1 className="text-2xl font-light text-gray-800 mb-6">부가세 신고</h1>

      {/* 기간 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 flex items-center gap-3">
        <input type="number" value={year} onChange={e => changePeriod(Number(e.target.value), half)}
          className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {(['상반기', '하반기'] as Half[]).map(h => (
            <button key={h} onClick={() => changePeriod(year, h)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium ${half === h ? 'bg-jeju-brown text-white' : 'text-gray-500'}`}>
              {h}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-400 ml-2">{months[0]} ~ {months[5]}</span>
        <div className="ml-auto flex items-center gap-3">
          {saved && <span className="text-xs text-gray-400">자동 저장됨</span>}
          <button onClick={resetPeriod}
            className="px-3 py-1.5 text-xs border border-gray-300 text-gray-500 rounded-lg hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-colors">
            초기화
          </button>
        </div>
      </div>

      {/* 업로드 슬롯 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 space-y-2">
        <UploadSlot label={NAVER_LABELS.제주} loaded={naver.제주.length} onFiles={files => handleNaver('제주', files)} />
        <UploadSlot label={NAVER_LABELS.남해} loaded={naver.남해.length} onFiles={files => handleNaver('남해', files)} />
        <UploadSlot label={NAVER_LABELS.해피빈} loaded={naver.해피빈.length} onFiles={files => handleNaver('해피빈', files)} />
        <details className="pl-1">
          <summary className="cursor-pointer text-xs text-gray-400 hover:text-jeju-brown">해피빈 엑셀 내보내기 방법</summary>
          <ol className="list-decimal list-inside mt-1 space-y-0.5 text-xs text-gray-500">
            <li>네이버페이 센터에서 세금신고 내역(부가세)을 기간 조회</li>
            <li>엑셀로 내려받아 위 슬롯에 그대로 업로드</li>
          </ol>
          <a href="https://admin.pay.naver.com/front/m/v2" target="_blank" rel="noreferrer"
            className="inline-block mt-1 text-xs text-jeju-brown hover:underline">네이버페이 센터 바로가기 ↗</a>
        </details>
        <UploadSlot label="쿠팡" loaded={coupang.length} onFiles={handleCoupang} />
        <UploadSlot label="오프라인(이지체크)" loaded={easyCheck.length} onFiles={handleEasy} />
        <details className="pl-1">
          <summary className="cursor-pointer text-xs text-gray-400 hover:text-jeju-brown">오프라인(이지체크) 엑셀 내보내기 방법</summary>
          <ol className="list-decimal list-inside mt-1 space-y-0.5 text-xs text-gray-500">
            <li>카드매출/입금관리 → 리뉴얼_부가세참조자료</li>
            <li>일별(1일~말일)로 조회</li>
            <li>조회한 항목에서 오른쪽 클릭 → 엑셀로 내보내기</li>
            <li>내려받은 엑셀을 위 슬롯에 그대로 업로드 (총합계·소계 행은 자동 제외됩니다)</li>
          </ol>
          <a href="https://www.easyshop.co.kr/taxMain/taxMain.kicc" target="_blank" rel="noreferrer"
            className="inline-block mt-1 text-xs text-jeju-brown hover:underline">이지체크샵 바로가기 ↗</a>
        </details>
        <UploadSlot label="홈택스" loaded={hometax.length} onFiles={handleHometax} />
        <p className="text-xs text-gray-400 pt-1">※ 홈택스는 매출 세금계산서(과세)·계산서(면세) 파일을 한 번에 선택하세요.</p>
      </div>

      {/* 텀블벅 수동입력 (전부 면세) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="text-sm font-medium text-gray-700 mb-3"><span>텀블벅</span> (수동 입력 · 전부 면세)</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500">
              <th className="text-left py-1">기간</th>
              <th className="text-right py-1">면세 매출</th>
            </tr>
          </thead>
          <tbody>
            {months.map(m => (
              <tr key={m}>
                <td className="py-1 text-gray-700">{m}</td>
                <td className="py-1 text-right">
                  <input type="number" value={tumblbugFor(m).exempt || ''} onChange={e => setTumblbugCell(m, Number(e.target.value))}
                    className="w-40 border border-gray-200 rounded px-2 py-1 text-right" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 총 합계 미리보기 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="px-6 py-3 border-b border-gray-200 text-sm font-medium text-gray-700">총 합계</div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-xs text-gray-500">
              <th className="text-left px-4 py-2">기간</th>
              <th className="text-right px-4 py-2">합계</th>
              <th className="text-right px-4 py-2">과세매출금액</th>
              <th className="text-right px-4 py-2">면세매출금액</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {total.map(t => (
              <tr key={t.month}>
                <td className="px-4 py-2 text-gray-700">{t.month}</td>
                <td className="px-4 py-2 text-right font-medium">{fmt(t.total)}</td>
                <td className="px-4 py-2 text-right">{fmt(t.taxable)}</td>
                <td className="px-4 py-2 text-right">{fmt(t.exempt)}</td>
              </tr>
            ))}
            <tr className="bg-gray-50 font-semibold">
              <td className="px-4 py-2">총 합계</td>
              <td className="px-4 py-2 text-right">{fmt(total.reduce((a, t) => a + t.total, 0))}</td>
              <td className="px-4 py-2 text-right">{fmt(total.reduce((a, t) => a + t.taxable, 0))}</td>
              <td className="px-4 py-2 text-right">{fmt(total.reduce((a, t) => a + t.exempt, 0))}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <button onClick={download}
        className="px-5 py-2.5 bg-jeju-brown text-white rounded-lg text-sm font-semibold hover:opacity-90">
        엑셀 다운로드
      </button>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-jeju-brown text-white px-6 py-2.5 rounded-lg text-sm font-semibold z-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
